import {
  fetchSignInMethodsForEmail as fetchSignInMethodsForEmailFirebase,
  getAuth,
  getIdToken,
  sendPasswordResetEmail as sendPasswordResetEmailFirebase,
  signInWithEmailAndPassword as signInWithEmailAndPasswordFirebase,
  signOut as signOutFirebase,
  updatePassword as updatePasswordFirebase,
} from 'firebase/auth';

import { showRequestTimeoutAlert } from '@/libs/errors/request-timeout';
import { captureException } from '@/libs/logger';
import { ensureOnline } from '@/libs/network';
import { getFirebaseApp } from '../init.web';

const auth = () => getAuth(getFirebaseApp());

const AUTH_REQUEST_TIMEOUT_MS = 15_000;

export class AuthTimeoutError extends Error {
  readonly code = 'auth/timeout';

  readonly alertHandled = true;

  constructor(label: string, timeoutMs: number) {
    super(`Firebase auth request timed out after ${timeoutMs}ms: ${label}`);
    this.name = 'AuthTimeoutError';
  }
}

const showTimeoutAlert = () => {
  showRequestTimeoutAlert();
};

type WithAuthTimeoutOptions = {
  timeoutMs?: number;
  silent?: boolean;
};

const withAuthTimeout = <T>(
  promise: Promise<T>,
  label: string,
  { timeoutMs = AUTH_REQUEST_TIMEOUT_MS, silent = false }: WithAuthTimeoutOptions = {},
): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      if (!silent) showTimeoutAlert();
      reject(new AuthTimeoutError(label, timeoutMs));
    }, timeoutMs);
  });
  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timer) clearTimeout(timer);
  });
};

export const signInWithEmailAndPassword = async (email: string, password: string) => {
  await ensureOnline();
  try {
    return await withAuthTimeout(
      signInWithEmailAndPasswordFirebase(auth(), email, password),
      'signInWithEmailAndPassword',
    );
  } catch (error) {
    captureException(
      {
        error_payload: error,
      },
      { tags: { usk_log_type: 'sign_in_with_email_and_password_error' } },
    );
    throw error;
  }
};

export const fetchSignInMethodsForEmail = async (email: string) => {
  await ensureOnline();
  try {
    return await withAuthTimeout(
      fetchSignInMethodsForEmailFirebase(auth(), email),
      'fetchSignInMethodsForEmail',
    );
  } catch (error) {
    captureException(
      {
        error_payload: error,
      },
      { tags: { usk_log_type: 'fetch_sign_in_methods_for_email_error' } },
    );
    throw error;
  }
};

export const signOut = async () => {
  try {
    return await withAuthTimeout(signOutFirebase(auth()), 'signOut');
  } catch (error) {
    captureException(
      {
        error_payload: error,
      },
      { tags: { usk_log_type: 'sign_out_error' } },
    );
    throw error;
  }
};

export const sendPasswordResetEmail = async (email: string) => {
  await ensureOnline();
  try {
    return await withAuthTimeout(
      sendPasswordResetEmailFirebase(auth(), email),
      'sendPasswordResetEmail',
    );
  } catch (error) {
    captureException(
      {
        error_payload: error,
      },
      { tags: { usk_log_type: 'send_password_reset_email_error' } },
    );
    throw error;
  }
};

export const getAuthToken = async () => {
  try {
    const currentUser = auth().currentUser;
    if (!currentUser) return null;
    return await withAuthTimeout(getIdToken(currentUser), 'getAuthToken', { silent: true });
  } catch (error) {
    captureException(
      {
        error_payload: error,
      },
      { tags: { usk_log_type: 'get_auth_token_error' } },
    );
    throw error;
  }
};

export const updateCurrentUserPassword = async (newPassword: string) => {
  await ensureOnline();
  try {
    const user = auth().currentUser;
    if (!user) {
      throw new Error('auth/no-current-user');
    }
    return await withAuthTimeout(
      updatePasswordFirebase(user, newPassword),
      'updateCurrentUserPassword',
    );
  } catch (error) {
    captureException(
      {
        error_payload: error,
      },
      { tags: { usk_log_type: 'update_current_user_password_error' } },
    );
    throw error;
  }
};

export const resetCurrentPassword = async ({
  email,
  currentPassword,
  newPassword,
}: {
  email: string;
  currentPassword: string;
  newPassword: string;
}) => {
  try {
    await signInWithEmailAndPassword(email, currentPassword);
    await updateCurrentUserPassword(newPassword);
    return true;
  } catch (error) {
    captureException(
      {
        error_payload: error,
      },
      { tags: { usk_log_type: 'reset_current_password_error' } },
    );
    throw error;
  }
};
